import { useEffect, useMemo, useRef, useState } from 'react';
import { useEveAgent, type EveMessage } from 'eve/react';
import type { HandleMessageStreamEvent } from 'eve/client';
import { setConversationTitle, cancelTurn, isSessionClosed, isProtocolRejection } from '../lib/conversations';
import { useTheme } from '../lib/theme';
import { ChatActionsContext } from '../render/ChatActions';
import { EveAssistant, hasVisibleContent, isPublishedPage, sessionRequests } from '../render/EveAssistant';
import { Composer } from './Composer';
import { EmptyHero } from './EmptyHero';
import { UserMsg, ChatSkeleton, ClosedNotice, OutdatedNotice, ReportAnswer, StillWorking } from './Messages';
import { SIGNAL_BUBBLES } from '../data/seed';

/** The visible text a user message carries (its text parts, concatenated). */
function userText(m: EveMessage): string {
  return m.parts.map((p) => (p.type === 'text' && typeof p.text === 'string' ? p.text : '')).join('');
}

/**
 * Non-render tool calls a single turn may make before the watchdog cancels it.
 * A normal answer runs a handful (skill loads + a query or two); only a genuine
 * loop reaches this. Generous so a legitimately query-heavy turn isn't cut off.
 */
const TOOL_CALL_LIMIT = 40;

/**
 * How long a Stop waits for eve to settle the turn before the client settles it
 * itself. eve's own `turn.cancelled` lands in well under a second and keeps the
 * partial answer, so this is an order of magnitude above the normal case and
 * only runs when the cancel was not honoured — the case that used to hang the
 * composer forever. Kept short because this interval is dead time with the
 * composer disabled, which is what the user is trying to escape.
 */
const STOP_SETTLE_MS = 2_000;

/** Cap on the question carried into a fresh chat: long enough for a real
 *  question, short enough that the composer opens readable instead of as a wall
 *  of text. The closed thread stays on screen to copy anything longer from. */
const DRAFT_MAX_CHARS = 4000;

/** Explorer holds per-Hotspot and per-wallet on-chain records, which is what the
 *  composer note points at. Static app chrome, not an agent-supplied href. */
const HELIUM_WORLD_URL = 'https://world.helium.com';

/**
 * The draft a "Report a problem" click opens a fresh chat with.
 *
 * It names the session and the turn, which is what makes the run's trace
 * addressable. The turn id comes off the reported message rather than from
 * session state, so it names the turn that produced THAT answer: eve keeps no
 * correspondence between the last turn a tab saw start and the turn behind any
 * given message, and an id belonging to a different answer sends a triager to
 * the wrong trace.
 *
 * The user's description is the one part nothing else can supply, so this is a
 * draft and is never sent for them.
 */
function reportDraft(sessionId: string | undefined, turnId: string | undefined): string {
  const where = [sessionId && `session ${sessionId}`, turnId && `turn ${turnId}`]
    .filter(Boolean)
    .join(', ');
  return ['File a bug about the answer I just got.', '', 'What went wrong: ', '', `(For the trace: ${where}.)`].join(
    '\n',
  );
}

/**
 * A conversation to reopen. Sessions are ID-addressed, so `sessionId` is the
 * whole cursor: `events` are the session's prior stream events (fetched by the
 * parent) seeded as `initialEvents`, and their count is the stream index to
 * resume from.
 */
export interface RestoreInput {
  sessionId: string;
  events: HandleMessageStreamEvent[];
}

/**
 * The chat surface: empty-state hero or message thread + floating composer,
 * driven by one `useEveAgent` session. `useEveAgent` reads session config only
 * at construction, so switching conversations REMOUNTS this component (App keys
 * it by sessionId); `restore` then seeds the prior thread.
 *
 * `onTurnComplete` fires when a turn finishes so the parent can refresh the
 * conversations list (a brand-new conversation appears there after its first
 * turn).
 */
export function ChatView({
  restore,
  onSend,
  onTurnComplete,
  onBusyChange,
  onSessionId,
  initialPrompt,
  draft,
  onContinueInNewChat,
  onReport,
}: {
  restore?: RestoreInput;
  /** Fired with the text when the user sends a message. */
  onSend?: (prompt: string) => void;
  /** A prompt to auto-send once on mount for a fresh chat (e.g. the artifact
      wrapper's "Edit with AI" deep-link). Ignored when restoring a thread. */
  initialPrompt?: string;
  onTurnComplete?: () => void;
  /** Reports whether a turn is currently streaming, so the shell can show a
      live "active query" indicator in the sidebar. */
  onBusyChange?: (busy: boolean) => void;
  /** Reports the session id once the server assigns it (mid-first-turn for a
      fresh chat). Lets the shell know which conversation is on screen even
      before it's lifted into `active` — e.g. so deleting it clears the pane. */
  onSessionId?: (sessionId: string) => void;
  /** Text to open the composer with, editable and not auto-sent. Carries a
      closed conversation's last question into the fresh chat that replaces it. */
  draft?: string;
  /** Asked for when this conversation is closed and cannot take new messages,
      so the shell can open a fresh chat holding the given draft. */
  onContinueInNewChat?: (draft: string) => void;
  /** Asked for when the reader wants to report a problem with an answer, with
      the draft that names the run it happened on. Absent ⇒ no report action. */
  onReport?: (draft: string) => void;
}) {
  // For a brand-new conversation, title it from the user's FIRST question once
  // the first turn finishes (the agent's answer never becomes the title). The
  // server assigns the sessionId mid-turn; read it from the fresh `onFinish`
  // snapshot (the React `agent.session` state lags and left it undefined). The
  // ref (fed by `onSessionChange`) is a fallback for the same value.
  const firstPromptRef = useRef<string | null>(null);
  const titledRef = useRef(false);
  const sessionIdRef = useRef<string | undefined>(undefined);

  // Runaway-turn watchdog. A non-converging turn (e.g. the model re-querying a
  // name that can't resolve) would otherwise stream tool calls indefinitely. We
  // count NON-render tool results per turn — render_* calls are the answer, not
  // a loop, so a rich multi-block reply can't trip this — and cancel the turn
  // once it crosses the ceiling. `stopped` also drives the user-facing note and
  // is set by the manual Stop button.
  const toolCallsRef = useRef(0);
  // The turn a cancel should name. Undefined between requesting a turn and eve
  // naming it, which is the only window a cancel can be sent in without one.
  const turnIdRef = useRef<string | undefined>(undefined);
  const autoStoppedRef = useRef(false);
  const [stopped, setStopped] = useState<null | 'manual' | 'auto'>(null);
  // Whether the tab settled the turn itself because eve did not confirm the
  // cancel in time. That ends only what THIS tab waits on: an eve turn is a
  // durable server-side run, so unless eve accepted the cancel the work carries
  // on, and the note must not claim more than happened.
  const [stopUnconfirmed, setStopUnconfirmed] = useState(false);
  // Read inside the settle timeout, which would otherwise close over a stale `busy`.
  const busyRef = useRef(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // The local half of a Stop. Every turn this tab starts carries this signal, and
  // aborting it ends the tab's wait on that turn, settling it to `ready`, without
  // asking eve for anything: the durable run is untouched.
  const turnAbort = useRef<AbortController | undefined>(undefined);
  const currentSessionId = () => sessionIdRef.current ?? restore?.sessionId;

  const agent = useEveAgent({
    host: '/api',
    initialSession: restore
      ? {
          sessionId: restore.sessionId,
          streamIndex: restore.events.length,
        }
      : undefined,
    initialEvents: restore?.events,
    onSessionChange: (session) => {
      // The cursor is absent until the session exists; a fresh chat reports none
      // until its first turn is accepted.
      if (session?.sessionId) {
        sessionIdRef.current = session.sessionId;
        onSessionId?.(session.sessionId);
      }
    },
    onEvent: (event) => {
      // New turn: reset the per-turn counters and clear any prior stop note.
      if (event.type === 'turn.started') {
        toolCallsRef.current = 0;
        autoStoppedRef.current = false;
        turnIdRef.current = event.data.turnId;
        setStopped(null);
        return;
      }
      if (event.type !== 'action.result') return;
      // One result per call the model made: a connection tool called through
      // eve's catalog is reported once, under its own qualified name.
      // `result` is a union; only tool-result carries a toolName. Read it
      // defensively and skip the answer-rendering render_* tools.
      const result = event.data.result as { toolName?: string };
      if (result.toolName?.startsWith('render_')) return;
      toolCallsRef.current += 1;
      const sid = currentSessionId();
      if (toolCallsRef.current > TOOL_CALL_LIMIT && !autoStoppedRef.current && sid) {
        autoStoppedRef.current = true;
        setStopped('auto');
        haltTurn(); // same bounded settle as the button — the watchdog rides the same cancel
      }
    },
    onFinish: (snapshot) => {
      const prompt = firstPromptRef.current;
      const sessionId = snapshot.session?.sessionId ?? sessionIdRef.current;
      // New conversation, first turn: summarize the question into a title, then
      // refresh the list so it appears titled. Restored threads already have one.
      if (!restore && !titledRef.current && prompt && sessionId) {
        titledRef.current = true;
        void setConversationTitle(sessionId, prompt).finally(() => onTurnComplete?.());
        return;
      }
      onTurnComplete?.();
    },
  });

  // Where each HITL request stands, so controls show only on a request that can
  // still be answered, live and on a restored thread.
  const requests = useMemo(() => sessionRequests(agent.data.inputs, agent.events), [agent.data.inputs, agent.events]);
  const messages = agent.data.messages;
  // eve keeps a turn open, and the store `streaming`, while that turn waits on a
  // person: a question or approval it raised stays answerable inside the same
  // turn. That wait is the user's move, not the agent's work, so it is not busy:
  // the composer is live, nothing shows as still working, and Stop is not offered
  // over a turn that is doing nothing.
  // Scoped to the active turn's own open requests: `waiting` is also set while a
  // turn waits on its tasks, and a request from an earlier turn is not this one's.
  const awaitingInput = useMemo(() => {
    const active = agent.data.activeTurnId;
    return active !== undefined && agent.data.turns[active]?.waiting === true && requests.openInTurn(active);
  }, [agent.data, requests]);
  // A turn this tab settled itself after an unconfirmed Stop is over for the tab
  // even while eve's session stream goes on reporting it.
  const busy = (agent.status === 'submitted' || agent.status === 'streaming') && !awaitingInput && !stopUnconfirmed;
  // A closed session rejects every follow-up, so this is not the generic
  // transport error and "try again" would be a lie: the thread is readable but
  // permanently unwritable, and the way forward is a fresh chat.
  const sessionClosed = agent.status === 'error' && isSessionClosed(agent.error);
  // The other error "try again" would be a lie about: the agent refused the
  // request shape, so this tab has to reload onto the current bundle first.
  const outdated = agent.status === 'error' && isProtocolRejection(agent.error);
  // Whether this thread published an interactive page. A page belongs to its
  // creator rather than to the conversation, so it stays editable after the
  // session closes — worth saying, but only where it's true.
  const publishedAPage = useMemo(() => messages.some((m) => m.parts.some(isPublishedPage)), [messages]);
  const [theme] = useTheme();

  // Surface the streaming state to the shell (sidebar "active query" indicator),
  // and clear it if this view unmounts mid-turn (switching conversations
  // remounts ChatView) so the indicator never sticks.
  useEffect(() => {
    busyRef.current = busy;
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  useEffect(() => () => clearTimeout(settleTimer.current), []);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  const anchorRef = useRef<HTMLDivElement>(null);

  // A restored thread seeds all its messages at mount (0→N in one tick), so a
  // smooth anchor scroll would animate up from the top of the page — janky.
  // Skip the animation for that first run: jump straight to the latest exchange.
  // Only live sends (later runs) smooth-anchor the new question to the top.
  const restoreScrollPending = useRef(Boolean(restore));

  // Anchor the latest question to the top of the view (so you read the answer
  // from its start as it streams in) rather than jumping to the bottom.
  useEffect(() => {
    const behavior = restoreScrollPending.current ? 'auto' : 'smooth';
    restoreScrollPending.current = false;
    anchorRef.current?.scrollIntoView({ block: 'start', behavior });
  }, [messages.length, busy]);

  function send(q: string) {
    // Refused here, at the one entry point every caller shares, and not per
    // caller: the composer blocks empties already, but a `render_follow` chip
    // sends its label verbatim and that label is model-supplied. `blockProps.Follow`
    // admits an empty string, and a chip renders from a part's streaming `input`,
    // which is raw tool args no schema has validated yet — so the annotation on `q`
    // does not hold for that caller and a non-string has to degrade, not throw.
    // eve answers an empty message with the same 400 a stale request shape gets,
    // which would tell the user to reload over a chip still sitting there.
    const text = typeof q === 'string' ? q.trim() : '';
    if (!text) return;
    if (busy) return; // one turn at a time; the agent rejects an overlapping send
    beginTurn();
    // Remember the first prompt of a new conversation so onFinish can title it.
    if (!restore && firstPromptRef.current === null) firstPromptRef.current = text;
    onSend?.(text);
    // The user message is projected optimistically into `messages`; errors
    // surface via `agent.status === 'error'` / `agent.error`.
    void agent.send(text, { signal: turnAbort.current?.signal }).catch(() => {});
  }

  // Auto-send a seeded prompt once on mount (the wrapper's "Edit with AI"
  // deep-link). Guarded so it fires exactly once, never on a restored thread.
  const initialSentRef = useRef(false);
  useEffect(() => {
    if (initialPrompt && !restore && !initialSentRef.current) {
      initialSentRef.current = true;
      send(initialPrompt);
    }
    // send is intentionally omitted — this is a one-shot mount effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, restore]);

  // Manual Stop. Always has a visible effect: the note renders even when there is
  // no session to cancel, and `haltTurn` bounds the wait so `busy` cannot stay set.
  /**
   * A turn is starting. Every entry point must come through here: the settle
   * backstop is armed against ONE turn, and a timer left armed across turns
   * aborts the next one instead — silently, since the note it would explain
   * itself with is cleared on the same path.
   */
  function beginTurn() {
    // Forget the turn a cancel would name. eve ignores a cancel whose turnId
    // does not match the running turn — silently, and still answering 202 — so a
    // Stop pressed between here and the new turn's `turn.started` would name the
    // PREVIOUS turn and be dropped, leaving the run going and only the settle
    // backstop to notice. Unscoped, a cancel stops whatever turn is active when
    // it arrives. `send` is guarded on `!busy`, so that is normally the one just
    // requested, but after an unconfirmed Stop the tab no longer counts the
    // earlier run as busy while it may still be going, and an unscoped cancel
    // then stops that run.
    turnIdRef.current = undefined;
    setStopped(null);
    setStopUnconfirmed(false);
    clearTimeout(settleTimer.current);
    turnAbort.current = new AbortController();
  }

  function stop() {
    setStopped('manual');
    haltTurn();
  }

  /**
   * Ask eve to abort the durable run, then bound the wait on it settling.
   *
   * The server cancel is the only thing that truly halts a turn, so it goes
   * first and its result is honoured. But a cancel eve never acts on used to
   * leave `busy` set forever — button stuck on Stop, composer disabled, no note
   * — so if the turn hasn't settled shortly after, the tab stops waiting on it
   * (the turn's abort signal) and stops counting it as busy. That is a backstop,
   * not the mechanism: it is deliberately long enough that eve's own
   * `turn.cancelled` almost always wins, which keeps the partial answer and any
   * trailing events.
   */
  function haltTurn() {
    const sid = currentSessionId();
    if (!sid) {
      // No session id yet (a fresh chat's first moments), so there is nothing to
      // cancel. Settle locally and say so: eve may still create and run the turn.
      setStopUnconfirmed(true);
      turnAbort.current?.abort();
      return;
    }
    // Fire and forget: a delivery failure says nothing about whether the run
    // stopped, and the timer below is the only thing that observes that.
    void cancelTurn(sid, turnIdRef.current);
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      if (!busyRef.current) return; // eve settled it; nothing to force
      setStopUnconfirmed(true);
      turnAbort.current?.abort();
    }, STOP_SETTLE_MS);
  }

  // Answer a pending clarifying question (ask_question HITL). eve holds the turn
  // open on the input request, which this view does not count as busy (see
  // `awaitingInput`), and submitting the response resumes that same turn. Not
  // routed through `send`: this carries no user message, only the input response.
  function answer(requestId: string, optionId?: string, text?: string) {
    // Before beginTurn, which would disarm a pending Stop backstop and orphan the
    // running turn's abort signal for a response eve would refuse anyway.
    if (!requests.isOpen(requestId)) return;
    beginTurn(); // resuming a parked turn is a turn start: disarm the prior backstop
    // eve resumes the turn that asked, under the same id and with no new
    // `turn.started`, so a Stop from here names that turn.
    turnIdRef.current = agent.data.inputs[requestId]?.turnId;
    const response = { requestId, ...(optionId ? { optionId } : {}), ...(text ? { text } : {}) };
    void agent.respond([response], { signal: turnAbort.current?.signal }).catch(() => {});
  }

  const hasMsgs = messages.length > 0 || busy;
  const lastUserIndex = messages.reduce((acc, m, i) => (m.role === 'user' ? i : acc), -1);
  // The question to carry into the fresh chat that replaces a closed one, so the
  // user re-asks by pressing send rather than retyping.
  const continueDraft = lastUserIndex >= 0 ? userText(messages[lastUserIndex]!).trim().slice(0, DRAFT_MAX_CHARS) : '';
  // Show the skeleton until the assistant turn has something to show. The
  // assistant message object appears (with only a `step-start`) a beat before
  // its first work/content part, so gating on `role === 'user'` alone blanked
  // the view for those seconds; keep the skeleton until a visible part lands and
  // <ActivityFeed>'s own pulse takes over.
  const last = messages[messages.length - 1];
  const showThinking = busy && (!last || last.role === 'user' || !hasVisibleContent(last));

  return (
    <ChatActionsContext.Provider value={{ onPrompt: send, onAnswer: answer }}>
      {!hasMsgs ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
          <EmptyHero onSend={send} bubbles={SIGNAL_BUBBLES} draft={draft} />
        </div>
      ) : (
        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          {/* Scrollable messages fill the area. The extra bottom padding lets
              the last turn scroll up clear of the floating composer below. */}
          <div style={{ position: 'absolute', inset: 0, overflowY: 'auto' }}>
            <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 24px 150px', display: 'flex', flexDirection: 'column', gap: 26 }}>
              {messages.map((m, i) => (
                <div
                  key={m.id}
                  ref={i === lastUserIndex ? anchorRef : undefined}
                  style={i === lastUserIndex ? { scrollMarginTop: 12 } : undefined}
                >
                  {m.role === 'user' ? (
                    <UserMsg text={userText(m)} />
                  ) : (
                    <>
                      <EveAssistant
                        message={m}
                        streaming={busy && i === messages.length - 1}
                        requests={requests}
                      />
                      {/* Withheld while this answer is still streaming, since
                          there is nothing to judge yet. A turn that failed or
                          was stopped is reportable, which is the point. */}
                      {onReport && hasVisibleContent(m) && !(busy && i === messages.length - 1) && (
                        <ReportAnswer
                          onReport={() => onReport(reportDraft(currentSessionId(), m.metadata?.turnId))}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
              {/* One or the other, never both: the skeleton covers the wait for
                  the first visible part, StillWorking the rest of the turn. */}
              {showThinking ? <ChatSkeleton /> : busy ? <StillWorking /> : null}
              {sessionClosed ? (
                <ClosedNotice
                  hasPage={publishedAPage}
                  onContinue={onContinueInNewChat && (() => onContinueInNewChat(continueDraft))}
                />
              ) : outdated ? (
                <OutdatedNotice />
              ) : agent.status === 'error' ? (
                <div style={{ color: 'var(--fg-2)', fontSize: 14.5 }}>
                  Sorry, I couldn’t reach Helium AI just now. Please try again.
                </div>
              ) : null}
              {!busy && stopped && (
                <div style={{ color: 'var(--fg-2)', fontSize: 14.5 }}>
                  {stopped === 'auto'
                    ? 'Stopped — this was taking longer than expected. Try narrowing the question.'
                    : 'Stopped.'}
                  {stopUnconfirmed && ' The agent didn’t confirm, so it may still be finishing this one.'}
                </div>
              )}
            </div>
          </div>
          {/* Floating composer: messages scroll behind it and fade into the
              page via the gradient, so there's no hard cut-off. The gradient
              strip is click-through; only the composer area is interactive. */}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
            <div style={{ height: 40, background: 'linear-gradient(to bottom, transparent, var(--bg-2))' }} />
            <div style={{ background: 'var(--bg-2)', padding: '0 24px 20px', pointerEvents: 'auto' }}>
              <div style={{ maxWidth: 860, margin: '0 auto' }}>
                {/* autoFocus: the hero's first send swaps to this composer (a
                    separate mount), so focus has to follow or typing stops
                    landing anywhere. Same on reopening a thread. */}
                {/* A closed conversation can't take a message, so the composer is
                    disabled rather than accepting text that would only fail. */}
                <Composer
                  onSend={send}
                  onStop={stop}
                  theme={theme}
                  compact
                  disabled={busy || sessionClosed}
                  autoFocus
                  initialValue={draft}
                />
                {/* Most answers here are warehouse aggregates, and Explorer has no
                    per-record view to check one against — so this names what it
                    does hold rather than asking for a verification it cannot
                    serve, and links it for a reader who hasn't met it. */}
                <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--fg-3)', marginTop: 8 }}>
                  Helium AI can make mistakes. Per-Hotspot and wallet records are on{' '}
                  <a
                    href={HELIUM_WORLD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    Helium World
                  </a>
                  .
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </ChatActionsContext.Provider>
  );
}
